import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Calendar, Loader2, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';

const Journal = () => {
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [patterns, setPatterns] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/journal`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Error loading entries:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.trim()) {
      setError('Please write something in your journal');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/journal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newEntry })
      });

      if (response.ok) {
        setSuccess('Journal entry saved successfully!');
        setNewEntry('');
        loadEntries();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Journal error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const analyzePatterns = async () => {
    setIsAnalyzing(true);
    setError('');

    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/analyze-patterns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPatterns(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to analyze patterns');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen className="w-8 h-8 text-yellow-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">Smart Journaling</h2>
          <p className="text-gray-400">Track your wellness journey</p>
        </div>
      </div>

      {/* New Entry Form */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-white mb-4">New Entry</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <textarea
            value={newEntry}
            onChange={(e) => setNewEntry(e.target.value)}
            placeholder="How are you feeling today? Write about your stress, symptoms, mood, or anything on your mind..."
            className="w-full h-40 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none resize-none"
          />

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-400">{success}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 py-4 text-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 mr-2" />
                Save Entry
              </>
            )}
          </Button>
        </form>
      </div>

      {/* AI Pattern Analysis */}
      {entries.length > 0 && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white">AI Pattern Analysis</h3>
            <Button
              onClick={analyzePatterns}
              disabled={isAnalyzing}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Analyze Patterns
                </>
              )}
            </Button>
          </div>

          {patterns && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <p className="text-gray-300 whitespace-pre-line">{patterns.analysis}</p>
            </div>
          )}
        </div>
      )}

      {/* Journal Entries */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-white">Previous Entries</h3>
        {entries.length === 0 ? (
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No journal entries yet. Start writing your first entry!</p>
          </div>
        ) : (
          entries.map((entry, index) => (
            <div key={index} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(entry.created_at)}</span>
              </div>
              <p className="text-gray-300 whitespace-pre-line">{entry.content}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Journal;
